import { useEffect, useRef, useState } from "react"

import cls from "classname"
import { useTranslation } from "react-i18next"

import usePrefersReducedMotion from "../../../../hooks/usePrefersReducedMotion"
import { blockAnchor } from "../../constants"

import styles from "./sectionNav.module.less"

// A block counts as in view once its top is this close under the top of the scroll area: a jump
// stops 32px under it (scroll-margin-top), plus the prototype's 24px.
const IN_VIEW_UNDER = 56
const CLOSE_DELAY = 180

// The admin layout scrolls #main-content; where it does not overflow (narrow windows), the window.
const scrollArea = () => {
  const main = document.getElementById("main-content")

  return main && main.scrollHeight > main.clientHeight ? main : null
}

// Only a page that scrolls has a bottom: on one that fits the screen the first block stays active.
const isScrolledToBottom = (area) => {
  if (area) return area.scrollTop + area.clientHeight >= area.scrollHeight - 4

  const page = document.documentElement

  if (page.scrollHeight <= window.innerHeight) return false

  return window.innerHeight + window.scrollY >= page.scrollHeight - 4
}

// The block whose top has passed the top of the scroll area last; the last block at the bottom of
// the page; the clicked block while its scroll runs and it is still on screen.
const blockInView = (blocks, lockedId) => {
  const area = scrollArea()
  const areaTop = area ? area.getBoundingClientRect().top : 0
  let current = blocks[0].id

  for (const block of blocks) {
    const section = document.getElementById(blockAnchor(block.id))

    if (section && section.getBoundingClientRect().top - areaTop <= IN_VIEW_UNDER)
      current = block.id
  }

  if (isScrolledToBottom(area)) current = blocks[blocks.length - 1].id

  const locked = lockedId && document.getElementById(blockAnchor(lockedId))

  if (locked) {
    const rect = locked.getBoundingClientRect()

    if (rect.top < window.innerHeight * 0.7 && rect.bottom > areaTop) current = lockedId
  }

  return current
}

// Right-side section navigation (DAA-1-14): a tick per block at the right edge that expands into a
// card of the block names on hover or keyboard focus and closes 180 ms after the pointer leaves. A
// click scrolls smoothly to the block (jumps with reduced motion); the block in view is
// highlighted. Hidden below 700 px.
const SectionNav = ({ blocks }) => {
  const { t } = useTranslation()
  const prefersReducedMotion = usePrefersReducedMotion()
  const [isOpen, setIsOpen] = useState(false)
  const [activeId, setActiveId] = useState(blocks[0].id)
  const lockedIdRef = useRef(null)
  const closeTimerRef = useRef(null)

  useEffect(() => {
    const main = document.getElementById("main-content")
    const spy = () => setActiveId(blockInView(blocks, lockedIdRef.current))
    // A wheel, touch or key press is the reader taking over: the clicked block no longer leads.
    const unlock = () => {
      lockedIdRef.current = null
    }

    spy()
    main?.addEventListener("scroll", spy, { passive: true })
    window.addEventListener("scroll", spy, { passive: true })
    // A resize or a block that grows (charts loading) moves the blocks without a scroll.
    window.addEventListener("resize", spy)
    const resizeObserver = new ResizeObserver(spy)

    for (const block of blocks) {
      const section = document.getElementById(blockAnchor(block.id))

      if (section) resizeObserver.observe(section)
    }

    window.addEventListener("wheel", unlock, { passive: true })
    window.addEventListener("touchstart", unlock, { passive: true })
    window.addEventListener("keydown", unlock)

    return () => {
      main?.removeEventListener("scroll", spy)
      window.removeEventListener("scroll", spy)
      window.removeEventListener("resize", spy)
      resizeObserver.disconnect()
      window.removeEventListener("wheel", unlock)
      window.removeEventListener("touchstart", unlock)
      window.removeEventListener("keydown", unlock)
      clearTimeout(closeTimerRef.current)
    }
  }, [blocks])

  const open = () => {
    clearTimeout(closeTimerRef.current)
    setIsOpen(true)
  }

  const closeSoon = () => {
    clearTimeout(closeTimerRef.current)
    closeTimerRef.current = setTimeout(() => setIsOpen(false), CLOSE_DELAY)
  }

  const goTo = (event, blockId) => {
    event.preventDefault()

    lockedIdRef.current = blockId
    setActiveId(blockId)
    document
      .getElementById(blockAnchor(blockId))
      ?.scrollIntoView({ behavior: prefersReducedMotion ? "auto" : "smooth", block: "start" })
  }

  return (
    <nav
      className={cls(styles.nav, { [styles.open]: isOpen })}
      aria-label={t("Page sections")}
      onMouseEnter={open}
      onMouseLeave={closeSoon}
    >
      {blocks.map((block, index) => (
        <a
          key={block.id}
          href={`#${blockAnchor(block.id)}`}
          className={cls(styles.item, { [styles.active]: block.id === activeId })}
          style={{ "--i": index }}
          aria-current={block.id === activeId ? "location" : undefined}
          onClick={(event) => goTo(event, block.id)}
        >
          <span className={styles.label}>{t(block.title)}</span>
          <i className={styles.tick} aria-hidden="true" />
        </a>
      ))}
    </nav>
  )
}

export default SectionNav
